import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getHomeRouteForObjective } from "@/hooks/use-home-route";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { LastUsedLoginBadge } from "@/components/LastUsedLoginBadge";
import { getLastUsedLoginMethod } from "@/lib/auth/lastUsedLoginMethod";

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUsedLoginMethod] = useState(() =>
    getLastUsedLoginMethod(localStorage));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await login(email, password);
      const redirectParam = searchParams.get("redirect");
      const redirectPath = redirectParam || getHomeRouteForObjective(user.onboarding_objective);
      navigate(redirectPath);
    } catch {
      // Error handling is done in the auth context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/images/autoclipper-logo-full.png"
              alt="Autoclipper Logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">{t('login.title')}</CardTitle>
          <CardDescription className="text-gray-400">
            {t('login.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">
                {t('common.email')}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">
                {t('common.password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm text-green-400 hover:text-green-300 underline"
              >
                {t('login.forgotPassword')}
              </Link>
            </div>

            <Button
              type="submit"
              className="relative w-full bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold rounded-full shadow-lg"
              disabled={isLoading}
              aria-label={
                lastUsedLoginMethod === 'password'
                  ? `${isLoading ? t('login.submitting') : t('login.submitButton')} ${t('login.lastUsed')}`
                  : undefined
              }
            >
              {isLoading ? t('login.submitting') : t('login.submitButton')}
              {lastUsedLoginMethod === 'password' && (
                <LastUsedLoginBadge label={t('login.lastUsed')} />
              )}
            </Button>

            <div className="text-center text-sm text-gray-400">
              {t('login.noAccount')}{" "}
              <Link
                to={`/register${searchParams.get("redirect") ? `?redirect=${encodeURIComponent(searchParams.get("redirect")!)}` : ""}`}
                className="text-green-400 hover:text-green-300 underline"
              >
                {t('login.createAccount')}
              </Link>
            </div>
          </form>

          <div className="mt-4 space-y-3">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-600" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-gray-800 px-2 text-gray-400">{t('common.or')}</span>
              </div>
            </div>

            <GoogleSignInButton
              redirect={searchParams.get("redirect") || "/"}
              label={t('auth.loginWithGoogle')}
              lastUsedLabel={
                lastUsedLoginMethod === 'google'
                  ? t('login.lastUsed')
                  : undefined
              }
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
